import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import type { ReactiveController } from 'lit';

import { KanbanCard } from '../card.js';
import { KanbanGroup } from '../group.js';
import type { KanbanViewUILogic } from '../kanban-view-ui-logic.js';

/**
 * External drop surface for the Kanban view. Internal card reorder
 * now runs through the shared `LivePreviewReorderController` (see
 * `KanbanViewUILogic.startCardReorder`); this controller keeps the
 * hit-test + drop-preview API that cross-view DnD consumers
 * (`showIndicator`, `moveTo`) call into when dragging a row from
 * outside the kanban.
 */
export class KanbanDragController implements ReactiveController {
  get host() {
    return this.logic.ui$.value;
  }

  dropPreview = createDropPreview();

  getInsertPosition = (
    evt: MouseEvent
  ):
    | {
        group: KanbanGroup;
        rowKey: string;
        card?: KanbanCard;
        position: InsertToPosition;
      }
    | undefined => {
    const eles = document.elementsFromPoint(evt.x, evt.y);
    const target = eles.find(v => v instanceof KanbanGroup) as KanbanGroup;
    if (!target) return undefined;
    const rowKey = (target as unknown as HTMLElement).dataset['rowKey'];
    if (!rowKey) return undefined;
    const card = getCardByPoint(target, evt.y);
    return {
      group: target,
      rowKey,
      card,
      position: card
        ? {
            before: true,
            id: card.cardId,
          }
        : 'end',
    };
  };

  showIndicator = (
    evt: MouseEvent,
    self: KanbanCard | undefined
  ): { group: KanbanGroup; position: InsertToPosition } | undefined => {
    const position = this.getInsertPosition(evt);
    if (position) {
      this.dropPreview.display(position.group, self, position.card);
    } else {
      this.dropPreview.remove();
    }
    return position;
  };

  get scrollContainer() {
    const scrollContainer = this.logic.scrollContainer$.value;
    return scrollContainer;
  }

  constructor(private readonly logic: KanbanViewUILogic) {}

  hostConnected() {
    // Internal card reorder now lives on the card element via the
    // shared `LivePreviewReorderController` (see
    // KanbanViewUILogic.startCardReorder). This controller keeps only
    // the EXTERNAL drag-out API: `showIndicator`, `getInsertPosition`,
    // `moveTo` are still consumed by cross-view drops from outside
    // the kanban (edgeless DnD, drag from other views). The old
    // dragStart gesture that intercepted kanban-internal card drags
    // has been removed so the card's pointerdown handler owns the
    // gesture end-to-end.
  }
}

const createDropPreview = () => {
  const div = document.createElement('div');
  div.style.height = '2px';
  div.style.borderRadius = '1px';
  div.style.backgroundColor = 'var(--streamlain-primary-color)';
  div.style.boxShadow = '0px 0px 8px 0px rgba(30, 150, 235, 0.35)';
  return {
    display(
      group: KanbanGroup,
      self: KanbanCard | undefined,
      card?: KanbanCard
    ) {
      const target = card ?? group.querySelector('.add-card');
      if (!target) {
        console.error('`target` is not found');
        return;
      }
      if (target.previousElementSibling === self || target === self) {
        div.remove();
        return;
      }
      if (target.previousElementSibling === div) {
        return;
      }
      target.insertAdjacentElement('beforebegin', div);
    },
    remove() {
      div.remove();
    },
  };
};

const getCardByPoint = (
  group: KanbanGroup,
  y: number
): KanbanCard | undefined => {
  const cards = Array.from(
    group.querySelectorAll('affine-data-view-kanban-card')
  );
  const positions = cards.map(v => {
    const rect = v.getBoundingClientRect();
    return (rect.top + rect.bottom) / 2;
  });
  const index = positions.findIndex(v => v > y);
  return cards[index];
};
